<script setup lang="ts">
import LoadingSpin from "@/components/Common/LoadingSpin.vue";
import {default as templateRouter} from "@/router";
import {default as templateStore} from "@/store";
</script>

<script lang="ts">
import {defineComponent} from 'vue'
import {PassProperties} from "@/components/RegistrationPage/Card/PassProperties";
import store from "@/store";
import axios from "axios";
import router from "@/router";
import {
    CheckInputDataInterface,
    UsedPassInterface
} from "@/components/RegistrationPage/Response/CheckInputData";

export default defineComponent({
    name: 'PaymentInfo',

    data(): {
        isLoading: boolean
        email: string
        lastInput: {
            promocode: string
            email: string
        },
        inputCheckTimeout: number | null
        inputCheckResult: CheckInputDataInterface | null
        promocode: string
        promocodeResult: {
            priceOff: number | null
            percentOff: number | null
        }
        data: {
            orderId: string
            signature: string
        }
        order: {
            totalAmount: number
            itemName: string
            itemQuantity: number
            itemAmount: number
        }
        errors: {
            passNoSell: boolean
            promocode: boolean
            promocodeEmail: boolean
            promocodeUsed: boolean
            needEmail: boolean
            badEmail: boolean
            badReferral: boolean
            takenEmail: boolean
            validation: {
                email: boolean
            }
        }
    } {
        return {
            isLoading: false,
            inputCheckResult: null,
            lastInput: {
                promocode: '',
                email: '',
            },
            email: '',
            inputCheckTimeout: null,
            promocode: '',
            promocodeResult: {
                priceOff: null,
                percentOff: null,
            },
            data: {
                orderId: '',
                signature: '',
            },
            order: {
                totalAmount: 0,
                itemName: '',
                itemQuantity: 0,
                itemAmount: 0,
            },
            errors: {
                validation: {
                    email: false,
                },

                passNoSell: false,
                needEmail: false,
                badEmail: false,
                badReferral: false,
                takenEmail: false,
                promocode: false,
                promocodeEmail: false,
                promocodeUsed: false,
            },
        }
    },

    computed: {
        selectedPass(): PassProperties | null
        {
            return store.state.selectedPass
        },

        maxLevel(): number
        {
            let level = 0
            store.state.passes.forEach((pass: PassProperties) => {
                level = Math.max(
                    level,
                    pass.level,
                )
            })

            return level
        },

        purchasedPass(): PassProperties | null
        {
            for (const pass of store.state.passes) {
                if (pass.code === this.inputCheckResult?.pass?.code) {
                    return pass
                }
            }

            return null
        },

        prices(): {
            originalPrice: number
            finalPrice: number
        } {
            if (!this.selectedPass) {
                return {
                    originalPrice: 0,
                    finalPrice: 0,
                }
            }

            const prices = {
                originalPrice: this.selectedPass.basicPrice,
                finalPrice: this.selectedPass.basicPrice,
            }

            let discount = 0

            // if (this.selectedPass.isPlayersChoice) {
            //     discount += 10
            // }

            const usedPass: UsedPassInterface | null = this.inputCheckResult?.pass ?? null
            if (usedPass) {
                prices.originalPrice = this.selectedPass.price - (this.purchasedPass?.basicPrice ?? 0)
                discount += (usedPass.referralOf || router.currentRoute.value.params.referralCode) ? 10 : 0
                discount += (usedPass.usedPromocode?.percentOff ?? this.inputCheckResult?.promocode?.percentOff) ?? 0
            } else {
                discount += router.currentRoute.value.params.referralCode ? 10 : 0
                discount += this.inputCheckResult?.promocode?.percentOff ?? 0
            }

            if (discount === 0 && this.selectedPass.isPlayersChoice) {
                discount += 10
            }

            prices.finalPrice = Math.max(1, Math.floor((prices.originalPrice * (100 - discount) / 100)));



            return prices
        }
    },

    watch: {
        purchasedPass: {
            handler(): void
            {
                this.togglePurchase()
            },
        },

        selectedPass: {
            handler(): void
            {
                this.togglePurchase()
            },
        },
    },

    mounted(): void
    {
        const button = this.$refs.registerButton
        if (button instanceof HTMLElement) {
            setTimeout(() => {
                button.classList.add('active')

                setInterval(() => {
                    button.classList.contains('maxOpen')
                        ? button.classList.remove('maxOpen')
                        : button.classList.add('maxOpen')
                }, 2000)
            }, 500)
        }

        this.email = (router.currentRoute.value.query.email || '').toString().toLowerCase().trim()
        this.checkInput()
    },

    methods: {
        togglePurchase(): void
        {
            if ((this.purchasedPass?.price ?? 0) >= (this.selectedPass?.price || 0)) {
                store.state.canPurchase = false
                return;
            }

            store.state.canPurchase = true
        },

        clearErrors(): void
        {
            this.errors.passNoSell = false

            this.errors.badReferral = false

            this.errors.validation.email = false
            this.errors.badEmail = false
            this.errors.needEmail = false
            this.errors.takenEmail = false

            this.errors.promocode = false
            this.errors.promocodeEmail = false
            this.errors.promocodeUsed = false
        },

        checkInput(): void
        {
            const promocode = this.promocode.trim()
            const email = this.email.trim()

            if (this.lastInput.promocode === promocode && this.lastInput.email === email) {
                return
            }

            this.isLoading = true
            this.clearErrors()

            if (this.inputCheckTimeout) {
                clearTimeout(this.inputCheckTimeout)
            }

            this.lastInput = {
                promocode: promocode,
                email: email,
            }

            const referral = router.currentRoute.value.params.referralCode || ''

            this.inputCheckTimeout = setTimeout(() => {
                axios
                    .get(
                        `/api/game-pass/v1/check?email=${email}&promocode=${promocode}&referral=${referral}`,
                    )
                    .then(result => {
                        this.isLoading = false
                        this.inputCheckResult = result.data.data
                        store.state.prices = result.data.data.prices
                    })
                    .catch(() => {
                        this.isLoading = false
                    })
            }, 500)
        },

        submit(): void
        {
            if (!store.state.canPurchase) {
                return
            }

            if (this.isLoading) {
                return
            }

            this.isLoading = true

            this.clearErrors()

            const email = this.email.trim()
            if (email === '') {
                this.errors.needEmail = true
                this.isLoading = false
                return
            }

            axios
                .post(
                    '/api/game-pass/v1/payment/robokassa/init',
                    {
                        email: email,
                        promocode: this.promocode.trim(),
                        pass: this.selectedPass?.code,
                        referral: router.currentRoute.value.params.referralCode ?? null,
                    },
                )
                .then(result => {
                    this.isLoading = false
                    window.location = result.data.data.payment_url
                })
                .catch(error => {
                    console.warn(error)
                    console.warn(error.code)
                    console.warn(error.response.status)
                    console.warn(error.response.data.errors)

                    switch (error.code) {
                        case 'ERR_BAD_REQUEST':
                            switch (error.response.status) {
                                case 400:
                                    if (error.response.data.errors.promocode) {
                                        switch (error.response.data.errors.promocode) {
                                            case 'This promocode was already used.':
                                                this.errors.promocodeUsed = true
                                                break

                                            case 'Promocode does not exist.':
                                                this.errors.promocode = true
                                                break

                                            case 'Promocode must match certain email.':
                                                this.errors.promocodeEmail = true
                                                break

                                            default:
                                                break
                                        }
                                    }

                                    if (error.response.data.errors.pass) {
                                        switch (error.response.data.errors.pass) {
                                            case 'No':
                                                this.errors.passNoSell = true
                                                break

                                            default:
                                                break
                                        }
                                    }

                                    if (error.response.data.errors.email) {
                                        switch (error.response.data.errors.email) {
                                            case 'This value should not be blank.':
                                                this.errors.badEmail = true
                                                break

                                            case 'This value is not a valid email address.':
                                                this.errors.validation.email = true
                                                break

                                            case 'User with this e-mail has already purchased a pass.':
                                                this.errors.takenEmail = true
                                                break

                                            default:
                                                break
                                        }
                                    }

                                    if (error.response.data.errors.referral) {
                                        switch (error.response.data.errors.referral) {
                                            case 'You are not trying to hack our referral code system, are you?':
                                                this.errors.badReferral = true
                                                break

                                            default:
                                                break
                                        }
                                    }
                                    break;

                                default:
                                    break;
                            }
                            break;

                        default:
                            break;
                    }

                    this.isLoading = false
                })
        },
    },
})
</script>

<template>
    <div
        class="form"
        :class="{
            mobile: templateStore.state.pageSize.width <= templateStore.state.mobileVersionThresholdWidth,
        }"
        v-if="selectedPass"
    >
        <img class="smoke" :src="`/img/page/landing/registration/misc/smoke.png`">

        <form @submit.prevent="submit">
            <div class="mb-3">
                <div class="input-group input-group-email">
                    <span class="input-group-text" id="inputGroupPrepend3">
                        <i class="bi bi-envelope-at"></i>
                    </span>

                    <input
                        type="text"
                        class="form-control"
                        @change="checkInput"
                        @keyup="checkInput"
                        :class="{
                            'is-invalid': errors.needEmail || errors.validation.email || errors.takenEmail,
                            'is-valid': email.trim() !== '' && (!(errors.needEmail || errors.validation.email || errors.badEmail || errors.takenEmail))
                        }"
                        placeholder="Твой e-mail"
                        id="validationEmail"
                        aria-describedby="validationEmailFeedback"
                        required
                        v-model="email"
                    >
                </div>

                <div class="input-group">
                    <span class="input-group-text" id="inputGroupPrepend3">
                        <i class="bi bi-magic"></i>
                    </span>

                    <input
                        type="text"
                        class="form-control input-promocode"
                        @change="checkInput"
                        @keyup="checkInput"
                        placeholder="Промокод"
                        maxlength="30"
                        v-model="promocode"
                    >
                </div>

                <LoadingSpin v-if="isLoading"/>

                <template v-else>
                    <div class="messages">
                        <div
                            class="error text-danger"
                            style="text-align: justify"
                            v-if="errors.badReferral"
                        >
                            <p>Похоже, ты пытаешься использовать неработающую реферальную ссылку: скорее всего, её неправильно скопировали и обрезали последние символы в коде.</p>
                            <p>Можно сделать следующее:</p>

                            <ul style="text-align: left;">
                                <li>Обратиться к тому, кто дал тебе ссылку, и попросить проверить её корректность;</li>
                                <li>Поискать другую реферальную ссылку;</li>
                                <li>Повторить регистрацию без реферальной ссылки (для этого <router-link to="/register" @click="clearErrors()">кликни сюда</router-link>)</li>
                            </ul>
                        </div>

                        <div
                            class="error text-danger"
                            style="text-align: justify"
                            v-if="errors.passNoSell"
                        >
                            <p>Покупка новых пропусков отключена с началом ОБТ. Вы можете только проапгрейдить имеющийся пропуск.</p>
                        </div>

                        <div class="errors" key="errors" v-if="inputCheckResult">
                            <span
                                class="error text-danger"
                                v-if="
                                    inputCheckResult?.pass?.usedPromocode
                                    && promocode.trim() !== ''
                                "
                            >
                                Использование нового промокода недоступно - при регистрации использовался другой промокод
                            </span>

                            <span
                                class="error text-danger"
                                v-if="
                                    inputCheckResult?.pass?.referralOf
                                    && templateRouter.currentRoute.value.params.referralCode
                                "
                            >
                                Использование новой реферальной ссылки недоступно - при регистрации использовалась другая реферальная ссылка
                            </span>

                            <span
                                class="error text-danger"
                                v-else-if="
                                    !inputCheckResult?.pass?.usedPromocode
                                    && !inputCheckResult?.promocode
                                    && inputCheckResult?.request.promocode.toLowerCase() === promocode.trim().toLowerCase()
                                    && promocode.trim() !== ''"
                            >
                                Такого промокода нет
                            </span>
                        </div>




                        <template v-if="!inputCheckResult?.pass">
                            <div class="upgradeInfo text-info d-flex flex-column">
                                <span v-if="selectedPass.isPlayersChoice">
                                    Пометка «Выбор игроков» даёт скидку <span class="text-success">10%</span>
                                </span>

                                <span v-if="inputCheckResult?.promocode">
                                    Введённый промокод даёт скидку <span class="text-success">{{ inputCheckResult.promocode.percentOff }}%</span>
                                </span>

                                <span v-if="templateRouter.currentRoute.value.params.referralCode">
                                    Регистрация по реферальной ссылке даёт скидку <span class="text-success">10%</span>
                                </span>

                                <span v-if="
                                    (selectedPass.isPlayersChoice ? 1 : 0)
                                    + (inputCheckResult?.promocode ? 1 : 0)
                                    + (templateRouter.currentRoute.value.params.referralCode ? 1 : 0)
                                    > 1
                                "
                                >
                                    Суммарная скидка на покупку составляет
                                    <span class="text-success">
                                        {{
                                            (inputCheckResult?.promocode?.percentOff ?? 0)
                                            + (templateRouter.currentRoute.value.params.referralCode ? 10 : 0)
                                            + (selectedPass.isPlayersChoice ? 10 : 0)
                                        }}%
                                    </span>
                                </span>
                            </div>
                        </template>





                        <div class="upgradeInfo text-info d-flex flex-column" v-if="inputCheckResult && purchasedPass" key="reg">
                            <template v-if="purchasedPass.level === maxLevel">
                                <div class="purchasedPass">
                                    <div  style="text-decoration: underline;">У тебя максимально возможный уровень пропуска!</div>
                                    <div>
                                        На этот e-mail уже оформлен пропуск <span style="white-space: nowrap" :style="{color: purchasedPass.labelColor}">«{{ purchasedPass.name }}»</span>
                                    </div>
                                </div>
                            </template>

                            <template v-else>
                                <div class="purchasedPass">
                                    <div style="text-decoration: underline;">Доступен апгрейд!</div>
                                    <div>
                                        На этот e-mail уже оформлен пропуск <span style="white-space: nowrap" :style="{color: purchasedPass.labelColor}">«{{ purchasedPass.name }}»</span>
                                    </div>
                                </div>

                                <template
                                    v-if="selectedPass.price <= purchasedPass.price"
                                >
                                    <span>Ты можешь проапгрейдиться до более высокоуровневого пропуска:</span>

                                    <template
                                        v-for="pass in templateStore.state.passes"
                                        :key="pass.code"
                                    >
                                        <div
                                            class="suggestegOption"
                                            v-if="pass.level > purchasedPass.level"
                                            @click="templateStore.state.selectedPass = pass"
                                            :style="{
                                                color: pass.labelColor,
                                            }"
                                        >
                                            «{{ pass.name }}»
                                        </div>
                                    </template>
                                </template>
                            </template>
                        </div>


                        <template v-if="inputCheckResult?.pass">
                            <div class="upgradeInfo text-info d-flex flex-column">
                                <span v-if="selectedPass.isPlayersChoice">
                                    Пометка «Выбор игроков» даёт скидку <span class="text-success">10%</span>
                                </span>

                                <span v-if="inputCheckResult.pass.usedPromocode">
                                    При регистрации был использован промокод, дающий скидку <span class="text-success">{{ inputCheckResult.pass?.usedPromocode?.percentOff }}%</span>
                                </span>

                                <span v-else-if="inputCheckResult?.promocode">
                                    Введённый промокод даёт скидку <span class="text-success">{{ inputCheckResult.promocode?.percentOff }}%</span>
                                </span>

                                <span v-if="inputCheckResult.pass.referralOf">
                                    При регистрации была использована реферальная ссылка, дающая скидку <span class="text-success">10%</span>
                                </span>

                                <span v-else-if="templateRouter.currentRoute.value.params.referralCode">
                                    Регистрация по реферальной ссылке даёт скидку <span class="text-success">10%</span>
                                </span>

                                <span v-if="
                                    (selectedPass.isPlayersChoice ? 1 : 0)
                                    + ((
                                        inputCheckResult.pass.usedPromocode
                                        || inputCheckResult.promocode
                                        ) ? 1 : 0)
                                    + ((
                                        inputCheckResult.pass.referralOf
                                        || templateRouter.currentRoute.value.params.referralCode
                                        ) ? 1 : 0)
                                    > 1
                                ">
                                    Суммарная скидка на апгрейд составляет
                                    <span class="text-success">
                                        {{
                                            (
                                                (
                                                    inputCheckResult.pass.usedPromocode?.percentOff
                                                    ?? inputCheckResult.promocode?.percentOff
                                                )
                                                ?? 0
                                            )
                                            + (
                                                (
                                                    inputCheckResult.pass?.referralOf
                                                    || templateRouter.currentRoute.value.params.referralCode
                                                )
                                                    ? 10
                                                    : 0
                                            )
                                            + (selectedPass.isPlayersChoice ? 10 : 0)
                                        }}%
                                    </span>
                                </span>
                            </div>
                        </template>
                    </div>
                </template>
            </div>

            <div
                v-show="templateStore.state.canPurchase && !isLoading"
                class="d-flex flex-column justify-content-center align-items-center"
            >
                <button
                    type="submit"
                    class="registerButton noselect"
                >
                    <div class="content" ref="registerButton">
                        <div class="doorLeaf top"/>
                        <div class="doorLeaf bottom"/>
                        <div class="background absolute-fill">
                            <div v-if="purchasedPass" class="label">
                                <span>
                                    Апгрейд до уровня
                                    <span class="name" style="white-space: nowrap" :style="{color: selectedPass.labelColor}">«{{ selectedPass.name }}»</span>
                                </span>
                            </div>

                            <div v-else class="label">
                                <span>
                                    Купить пропуск
                                    <span class="name" style="white-space: nowrap" :style="{color: selectedPass.labelColor}">«{{ selectedPass.name }}»</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </button>

                <div class="price">
                    <template v-if="purchasedPass && templateStore.state.prices[selectedPass.code].upgradeFrom && templateStore.state.prices[selectedPass.code].upgradeFrom[purchasedPass.code]">
                        <span>Стоимость апгрейда: </span>

                        <div class="total">{{ templateStore.state.prices[selectedPass.code].upgradeFrom[purchasedPass.code].toLocaleString('ru').replace(/\u00A0/g, ' ') }} ₽</div>
                    </template>

                    <template v-else>
                        <span>Стоимость: </span>

                        <div class="result" v-if="selectedPass.basicPrice !== templateStore.state.prices[selectedPass.code].buy">
                            <div class="stroke">
                                <span>{{ selectedPass.basicPrice.toLocaleString('ru').replace(/\u00A0/g, ' ') }} ₽</span>
                                <div class="line"/>
                            </div>

                            <div class="total">{{ templateStore.state.prices[selectedPass.code].buy.toLocaleString('ru').replace(/\u00A0/g, ' ') }} ₽</div>
                        </div>

                        <div v-else class="total">{{ templateStore.state.prices[selectedPass.code].buy.toLocaleString('ru').replace(/\u00A0/g, ' ') }} ₽</div>
                    </template>
                </div>

                <span class="emailInfo text-secondary">На этот email ты получишь письмо о начале ЗБТ.</span>
            </div>
        </form>
    </div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.emailInfo {
    margin: 0 auto;
}

.suggestegOption {
    cursor: pointer;
    margin-top: 2px;
    padding: 5px;
    background: rgba(0, 255, 255, 0.07);
    border-radius: 4px;

    &:hover {
        background: rgba(0, 255, 255, 0.13);
    }
}

.upgradeInfo {
    display: flex;
    flex-direction: column;
    gap: 5px;

    .purchasedPass {
        border: 1px solid rgba(255, 255, 255, 0.2);
        background: rgba(0, 255, 255, 0.1);
        border-radius: 6px;
        padding: 10px;
    }
}

.mb-3 {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 0 0 5px 0 !important;
    width: 570px;
}

.form {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    &.mobile {
        .mb-3 {
            width: 350px;

            input {
                font-size: 14px;
            }
        }

        .price {
            font-size: 14px;
        }

        .registerButton {
            width: 350px;
            max-width: 350px;
            min-width: 350px;

            .label {
                font-size: 14px;
            }
        }
    }

    form {
        position: relative;
        z-index: 100;
    }

    .smoke {
        width: 1100px;
        height: 1100px;
        position: absolute;
        z-index: 0;
        animation: smoke-rotate 120s linear infinite;

        @keyframes smoke-rotate {
            from   {rotate: 0;}
            to {rotate: 360deg;}
        }
    }
}

.messages {
    width: 100%;
    font-size: 15px;
    line-height: 1.1;
    text-align: center;
    display: flex;
    flex-direction: column;
    margin: 0 0 5px 0;
    gap: 6px;

    .errors {
        display: flex;
        flex-direction: column;
    }
}

.group-outer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.input-group {
    flex-wrap: wrap;
    justify-content: center !important;
}

.input-promocode {
    text-transform: uppercase;
}

::-webkit-input-placeholder {
    text-transform: initial;
}

:-moz-placeholder {
    text-transform: initial;
}

::-moz-placeholder {
    text-transform: initial;
}

:-ms-input-placeholder {
    text-transform: initial;
}

.price {
    text-align: center;
    font-size: 16px;
    background: rgba(0, 0, 0, 0.6);
    border-bottom-right-radius: 7px;
    border-bottom-left-radius: 7px;
    padding: 5px 0 5px 0;
    box-shadow: 0 0 5px 1px rgba(0, 0, 0, 0.1);
    color: white;

    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;

    .result {
        display: flex;
        gap: 10px;
    }

    div.stroke {
        color: gray;
        text-decoration: line-through;
    }

    div.total {
        color: #00e300;
    }
}

.text-success {
    color: #00e300 !important;
}

.registerButton {
    position: relative;
    background: black;
    box-shadow: 0 0 10px 3px black;
    border-radius: 10px;
    cursor: pointer;
    margin: 0 auto;
    width: 570px;
    max-width: 570px;
    min-width: 570px;
    min-height: 60px;
    height: 60px;
    text-decoration: none;
    overflow: hidden !important;
    border: 1px solid black;
    transition: scale 0.2s ease;
    display: block;
    padding: 0;

    .content {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        .doorLeaf {
            background: linear-gradient(
                    135deg,
                    rgb(255, 184, 45) 0%,
                    rgba(30,30,30,1) 0.2%,
                    rgba(30,30,30,1) 5%,
                    rgb(255, 184, 45) 5.2%,
                    rgb(255, 184, 45) 10%,
                    rgba(30,30,30,1) 10.2%,
                    rgba(30,30,30,1) 15%,
                    rgb(255, 184, 45) 15.2%,
                    rgb(255, 184, 45) 20%,
                    rgba(30,30,30,1) 20.2%,
                    rgba(30,30,30,1) 25%,
                    rgb(255, 184, 45) 25.2%,
                    rgb(255, 184, 45) 30%,
                    rgba(30,30,30,1) 30.2%,
                    rgba(30,30,30,1) 35%,
                    rgb(255, 184, 45) 35.2%,
                    rgb(255, 184, 45) 40%,
                    rgba(30,30,30,1) 40.2%,
                    rgba(30,30,30,1) 45%,
                    rgb(255, 184, 45) 45.2%,
                    rgb(255, 184, 45) 50%,
                    rgba(30,30,30,1) 50.2%,
                    rgba(30,30,30,1) 55%,
                    rgb(255, 184, 45) 55.2%,
                    rgb(255, 184, 45) 60%,
                    rgba(30,30,30,1) 60.2%,
                    rgba(30,30,30,1) 65%,
                    rgb(255, 184, 45) 65.2%,
                    rgb(255, 184, 45) 70%,
                    rgba(30,30,30,1) 70.2%,
                    rgba(30,30,30,1) 75%,
                    rgb(255, 184, 45) 75.2%,
                    rgb(255, 184, 45) 80%,
                    rgba(30,30,30,1) 80.2%,
                    rgba(30,30,30,1) 85%,
                    rgb(255, 184, 45) 85.2%,
                    rgb(255, 184, 45) 90%,
                    rgba(30,30,30,1) 90.2%,
                    rgba(30,30,30,1) 95%,
                    rgb(255, 184, 45) 95.2%
            );

            z-index: 100;
            position: absolute;
            left: 0;
            right: 0;
            height: 40px;
            transition: all 0.2s ease;

            &.top {
                //top: -30px;
                top: -10px;
                left: -62px;
                box-shadow: inset 0 0 3px 1px black;
            }
            &.bottom {
                box-shadow: inset 0 0 3px 1px black;
                //bottom: -30px;
                bottom: -10px;
                right: -62px;
            }
        }

        .background {
            position: absolute;
            z-index: 0;
            border: 1px solid #161616;
            box-sizing: content-box;
            box-shadow: inset 0 0 15px 11px black;
            background: black;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.5s ease-in;
        }

        .label {
            position: relative;
            z-index: 1000;
            color: #d8ffef;
            font-size: 16px;
            text-shadow: 0 0 5px black;
            transition: color 0.1s ease-in;
            //background: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 25%, rgba(0,0,0,0.5) 75%, rgba(0,0,0,0) 100%);
            //width: 100%;
            //height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;


            .name {
                padding: 0 10px 3px 10px;
                background: rgba(0, 0, 0, 0.7);
                box-shadow: 0 0 7px 1px rgba(0, 0, 0, 0.6);
                border-radius: 7px;
            }
        }

        &.active {
            .doorLeaf.top {
                top: -31px;
            }
            .doorLeaf.bottom {
                bottom: -31px;
            }

            .background {
                box-shadow: inset 0 0 15px 11px #0076a8;
                background: #00a682;
            }
        }

        &.maxOpen,
        &:hover {
            .doorLeaf.top {
                top: -37px;
                border-bottom: 1px solid black;
            }

            .doorLeaf.bottom {
                bottom: -37px;
                border-top: 1px solid black;
            }
        }

        &:hover {
            .background {
                background: #00daa8;
                transition: background-color 0.3s ease;
            }

            .label {
                .name {
                    background: rgba(0, 0, 0, 0.9);
                }
            }
        }
    }

    &:active {
        .content {
            .background {
                background: #00ffc6;
                transition: background-color 0.01s ease;
            }
        }
    }
}
</style>
